
import React from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Diff } from "lucide-react";

interface DiffItem {
  path: string;
  oldValue: any;
  newValue: any;
  formattedOldValue: string;
  formattedNewValue: string;
  willBeOmitted?: boolean;
}

interface SessionDiffDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  diffItems: DiffItem[];
  title?: string;
}

export const SessionDiffDialog: React.FC<SessionDiffDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  diffItems,
  title = "Changes preview"
}) => {
  // Sort diffItems alphabetically by path
  const sortedDiffItems = [...diffItems].sort((a, b) => a.path.localeCompare(b.path));

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[700px] lg:max-w-[900px] h-[80vh] flex flex-col p-0 gap-0">
        <DialogHeader className="p-6 border-b border-gray-200 shadow-sm flex-shrink-0">
          <div className="flex items-center gap-2">
            <Diff className="h-5 w-5 text-muted-foreground" />
            <DialogTitle>{title}</DialogTitle>
          </div>
        </DialogHeader>
        
        <ScrollArea className="flex-1 p-6">
          {sortedDiffItems.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No changes detected
            </div>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-[1fr_1fr_3fr] gap-2 mb-2 font-medium">
                <div className="px-2 py-1">Field</div>
                <div className="px-2 py-1">Previous</div>
                <div className="px-2 py-1">New</div>
              </div>
              
              {sortedDiffItems.map((item, index) => (
                <div 
                  key={index} 
                  className="grid grid-cols-[1fr_1fr_3fr] gap-2 bg-muted/50 rounded-md"
                >
                  <div className="px-2 py-1 border-r border-border/30 font-mono text-xs break-all">
                    {item.path}
                    {item.willBeOmitted && (
                      <span className="block text-amber-600 text-[10px] italic mt-0.5">
                        Will be omitted from database
                      </span>
                    )}
                  </div>
                  <div className="px-2 py-1 border-r border-border/30 break-all whitespace-pre-wrap text-xs text-muted-foreground bg-red-50/30">
                    {item.formattedOldValue}
                  </div>
                  <div className={`px-2 py-1 break-all whitespace-pre-wrap text-xs ${
                    item.willBeOmitted ? "bg-amber-50/30 italic" : "bg-green-50/30"
                  }`}>
                    {item.formattedNewValue}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
        
        <DialogFooter className="p-6 border-t border-gray-200 shadow-sm flex-shrink-0">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            type="button"
            onClick={onConfirm}
          >
            Save with these changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
